/**
 * Browser entry point. Two things happen here before React ever renders, and
 * both are load-bearing:
 *
 *  - `./theme/tokens.css` is imported for its side effect (the `@theme`
 *    tokens, the density token sets, and the self-hosted `@font-face`) — the
 *    only stylesheet packages/web needs, and the only file allowed to contain
 *    a colour literal (design §2.1–2.3, enforced by `gridfitWeb/no-raw-colour`).
 *  - `setDensity(preferredDensity())` runs BEFORE `createRoot`, so `<html
 *    data-density>` is correct on the very first paint — a component never
 *    guesses its own density, and there is no flash of the wrong one.
 *
 * `registerServiceWorker()` (task 13, `pwa/register.ts`) is called
 * fire-and-forget, unawaited: registration is inherently asynchronous browser
 * work that must never gate the first paint, and the function is its own
 * no-op guard in the inner dev loop (`import.meta.env.PROD` is false there),
 * so calling it unconditionally here is safe in both loops — design §1.3.
 *
 * `startOutboxAutoDrain()` (task 17, `outbox/drain.ts`) also runs here,
 * exactly once — review round 1 moved it here FROM `api/hooks.ts`'s own
 * module scope, where it was measured unsound: `hooks.ts` is a module every
 * screen happens to import (to reach `commandHook`/`queryHook`), not a
 * deliberate boot sequence, so a TEST importing only that module and firing
 * `online`/`visibilitychange` attached real listeners with nothing to stop
 * it. Living here instead is the identical reasoning
 * `installGlobalErrorReporting()` below already gives for itself: one
 * registration for the page's whole lifetime, at the one place this app
 * already collects "runs once before anything renders" side effects.
 *
 * `QueryClientProvider` wraps `<App />` here with `api/queryClient.ts`'s
 * MODULE-SCOPE `queryClient` (Task 11) — that file existed since Task 9 but
 * had no provider wiring it in until now. Constructing a `QueryClient`
 * inside `App`'s render body instead is the exact v2 bug this rebuild's
 * non-migrating list names: v2 did that, so every re-render minted a fresh
 * cache, silently discarding whatever was already fetched. Importing the
 * already-created instance here, once, at the top of the tree, is what
 * keeps one cache alive for the app's whole lifetime.
 *
 * `installGlobalErrorReporting()` (task 12, `api/report.ts`) also runs
 * before `createRoot`, exactly once — it attaches `window`'s `error` and
 * `unhandledrejection` listeners, the two failure classes that never reach a
 * React error boundary (that function's own doc comment, `api/report.ts`,
 * says why). Calling it here, rather than inside `<App>` or a component, is
 * what keeps it to exactly one registration for the page's whole lifetime —
 * inside a component it would re-attach on every remount `<StrictMode>`
 * triggers in development.
 *
 * `requestPersistence()` (task 19, `outbox/clearance.ts`) — design §5.4:
 * "`navigator.storage.persist()` is requested at install". Fire-and-forget,
 * unawaited, the same shape `registerServiceWorker()` already uses just
 * above it: the browser's own answer (granted/denied/unavailable) is
 * inherently asynchronous and must never gate first paint, and the
 * function records its own outcome into `clearance.ts`'s module-scope store
 * for `SyncTray`'s detail view to read reactively — nothing here needs the
 * resolved value directly. Calling it unconditionally on every boot (not
 * just once ever) is safe: the underlying browser API is itself idempotent
 * (a browser that already decided does not re-prompt or re-ask), matching
 * that function's own doc comment.
 *
 * `beginClearanceCheck()` (task 19 fix round 1, `outbox/clearance.ts`) —
 * review's Important 1: this is the CORRECT moment to trigger storage-
 * clearance detection, and `useClearanceStatus()`'s own mount-time fallback
 * (still present, for this app's OWN test suite, which never imports this
 * file) is NOT a substitute for it in production. `SyncTray` — the only
 * mounter of that hook — lives inside `AppShell`'s signed-in branch, which
 * never renders for a signed-out visitor; a detector triggered only from
 * there can never observe a genuine `401` on a brand-new device, and would
 * misreport its very first sign-in as `'cleared'` instead of `'first-run'`.
 * Firing here, before React even renders, is what lets a truly fresh boot's
 * OWN `/api/me` check see the real, unauthenticated state — see that
 * function's own doc comment for the full trace. Like the two calls above,
 * this line is NOT covered by an app-integration test: `test/render.tsx`'s
 * `renderApp()` never imports `main.tsx` (the same accepted, pre-existing
 * gap `registerServiceWorker()`/`startOutboxAutoDrain()` already have here).
 */
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { App } from './App'
import { installGlobalErrorReporting } from './api/report'
import { queryClient } from './api/queryClient'
import { beginClearanceCheck, requestPersistence } from './outbox/clearance'
import { startOutboxAutoDrain } from './outbox/drain'
import { registerServiceWorker } from './pwa/register'
import './theme/tokens.css'
import { preferredDensity, setDensity } from './theme/density'

setDensity(preferredDensity())
installGlobalErrorReporting()
startOutboxAutoDrain()
void registerServiceWorker()
void requestPersistence()
beginClearanceCheck()

const root = document.getElementById('root')
if (!root) throw new Error('#root missing from index.html')

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
)
